import { defineSettingsProvider } from "./provider";
import {
  FlatTeamSettingsCatalogDefinitions,
  FlatTeamSettingsCatalogGroups,
} from "./providers/flat-team-settings";
import type {
  SettingDefinition,
  SettingGroupDefinition,
  SettingProductArea,
  SettingValueSource,
  TeamSettingsSettingValueSource,
} from "./types";

export type {
  ExternalSettingValueSource,
  SettingAccess,
  SettingAgentEditability,
  SettingAgentEditabilityMode,
  SettingAvailability,
  SettingAvailabilityCondition,
  SettingAvailabilityContext,
  SettingAvailabilityEvaluatorCondition,
  SettingAvailabilityEvaluatorDataRequirements,
  SettingAvailabilityEvaluatorId,
  SettingAvailabilityIntegrationKey,
  SettingAvailabilityMissingContextBehavior,
  SettingAvailabilitySettingEqualsCondition,
  SettingAvailabilityState,
  SettingAvailabilityUnavailableBehavior,
  SettingChangeGuidance,
  SettingDefinition,
  SettingDependency,
  SettingDependencyTarget,
  SettingDirectWriteGrant,
  SettingGroupChatSurface,
  SettingGroupDefinition,
  SettingGroupFullPageTarget,
  SettingMetadata,
  SettingOwner,
  SettingPermission,
  SettingProductArea,
  SettingProviderWriteGrant,
  SettingReadAccess,
  SettingReadValueVisibility,
  SettingRegistryMetadataInput,
  SettingRisk,
  SettingRiskInput,
  SettingRiskLevel,
  SettingSourceKind,
  SettingValueAdapterId,
  SettingValueSemantics,
  SettingValueSource,
  SettingVisibility,
  SettingWorkflowWriteGrant,
  SettingWriteGrant,
  SettingWriteGrantBase,
  TeamSettingsSettingValueSource,
} from "./types";

export {
  canDirectlyWriteSetting,
  canReadSetting,
  canViewSetting,
  getDirectSettingWriteGrants,
  getMostRestrictiveSettingVisibility,
  SettingVisibilityRank,
} from "./access";
export {
  evaluateSettingAvailability,
  getCombinedSettingAvailabilityEvaluatorDataRequirements,
  getSettingAvailabilityConditionEvaluatorIds,
  getSettingAvailabilityConditionSettingIds,
  getSettingAvailabilityEvaluatorDataRequirements,
  isSettingAvailabilitySearchVisible,
  SETTING_AVAILABILITY_EVALUATORS,
  SETTING_AVAILABILITY_UNAVAILABLE_BEHAVIORS,
  shouldShowSettingsForAvailability,
} from "./availability";
export {
  createSettingMetadataFromGroupDefaults,
  getDefaultSettingAgentEditability,
} from "./metadata";
export type {
  SettingMetadataAgentEditabilityResolver,
  SettingMetadataDefaultsContext,
} from "./metadata";
export type { SettingProvider } from "./provider";
export {
  SETTING_AVAILABILITY_EVALUATOR_IDS,
  SETTING_AVAILABILITY_INTEGRATION_KEYS,
  SETTING_AVAILABILITY_MISSING_CONTEXT_BEHAVIORS,
} from "./types";

export const SettingCatalogDefinitions = [
  ...FlatTeamSettingsCatalogDefinitions,
] as const satisfies readonly SettingDefinition[];

export const SettingCatalogGroups = [
  ...FlatTeamSettingsCatalogGroups,
] as const satisfies readonly SettingGroupDefinition[];

export const SettingCatalogProvider = defineSettingsProvider({
  name: "settings catalog",
  groups: SettingCatalogGroups,
  definitions: SettingCatalogDefinitions,
});

export const SettingCatalogDefinitionById: Readonly<
  Record<string, SettingDefinition>
> = SettingCatalogProvider.definitionById;

export function getSettingCatalogDefinition(
  id: string,
): SettingDefinition | undefined {
  return SettingCatalogDefinitionById[id];
}

export function getSettingCatalogGroup(
  groupId: string,
): SettingGroupDefinition | undefined {
  return SettingCatalogGroups.find((group) => group.id === groupId);
}

export function getSettingCatalogDefinitionsForGroup(
  groupId: string,
): readonly SettingDefinition[] {
  return SettingCatalogDefinitions.filter((definition) => {
    const metadata = definition.metadata;
    return (
      definition.groupId === groupId ||
      metadata.relatedGroups?.includes(groupId)
    );
  });
}

export function getSettingCatalogDefinitionsForTeamSettingsKey(
  key: string,
): readonly SettingDefinition[] {
  return SettingCatalogDefinitions.filter((definition) => {
    return (
      isTeamSettingsSettingValueSource(definition.valueSource) &&
      definition.valueSource.key === key
    );
  });
}

export function getSettingCatalogGroupsForProductArea(
  productArea: SettingProductArea,
): readonly SettingGroupDefinition[] {
  return SettingCatalogGroups.filter((group) => {
    return group.productArea === productArea;
  });
}

export function isTeamSettingsSettingValueSource(
  valueSource: SettingValueSource,
): valueSource is TeamSettingsSettingValueSource {
  return valueSource.adapter === "teamSettings";
}

export { defineSettingsProvider } from "./provider";
